// Pure helpers behind the mobile navigation (bottom tab bar, top bar title,
// "More" sheet) and the command palette. Kept free of React so the rules -
// which destinations get a tab, which tab is lit for a deep page, what a page
// is called - are unit tested.

// Destinations that earn a tab, in priority order. Whatever the user's role
// can actually see is taken from the top; everything else lives in "More".
export const TAB_PRIORITY = ['/home', '/tickets', '/users', '/invoices', '/quotes', '/subscriptions', '/leads', '/sales'];

export const pickTabs = (items, max = 4) => {
  const byPath = new Map(items.map((item) => [item.path, item]));
  const tabs = [];
  TAB_PRIORITY.forEach((path) => {
    if (tabs.length < max && byPath.has(path)) tabs.push(byPath.get(path));
  });
  return tabs;
};

// Detail / child pages light up their parent's tab.
const PARENT_PREFIXES = [
  ['/customer/', '/users'],
  ['/createcustomer', '/users'],
  ['/invoices/', '/invoices'],
  ['/addinvoice', '/invoices'],
  ['/tickets/', '/tickets'],
  ['/lead/', '/leads']
];

const startsWithSegment = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

const normalise = (pathname) => String(pathname || '').toLowerCase().replace(/\/+$/, '') || '/';

// Is `item` (with optional submenu) the section the user is currently in?
export const isItemActive = (item, pathname) => {
  const here = normalise(pathname);
  const paths = [item.path, ...((item.submenu || []).map((s) => s.path))].map(normalise);
  if (paths.some((p) => here === p || (p !== '/' && startsWithSegment(here, p)))) return true;
  const parent = PARENT_PREFIXES.find(([prefix]) => here.startsWith(prefix));
  return !!parent && paths.includes(parent[1]);
};

// Names for pages that aren't in the menu (detail pages, settings...).
const DETAIL_TITLES = [
  ['/tickets/', 'Ticket'],
  ['/customer/', 'Customer'],
  ['/invoices/editinvoice', 'Invoice'],
  ['/addinvoice', 'New Invoice'],
  ['/createcustomer', 'New Customer'],
  ['/lead/', 'Lead'],
  ['/settings', 'Settings'],
  ['/about', 'About']
];

const humanise = (segment) => String(segment || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

export const titleForPath = (pathname, items) => {
  const here = normalise(pathname);
  if (here === '/home' || here === '/') return 'Home';

  const detail = DETAIL_TITLES.find(([prefix]) => here.startsWith(prefix));
  if (detail) return detail[1];

  // A top-level item's own label wins over a child that points at the same
  // path ("Tickets", not "Ticket Overview").
  const top = items.find((item) => normalise(item.path) === here);
  if (top) return top.label;
  for (const item of items) {
    const child = (item.submenu || []).find((s) => normalise(s.path) === here);
    if (child) return child.label;
  }
  return humanise(here.split('/').filter(Boolean).pop()) || 'Blade';
};

// Pages one level below a section show a back arrow instead of the logo.
export const isDetailPath = (pathname) => {
  const here = normalise(pathname);
  return DETAIL_TITLES.some(([prefix]) => prefix !== '/about' && here.startsWith(prefix));
};

// Flat list for the command palette's "Go to" section.
export const flattenNav = (items, extra = []) => {
  const entries = [];
  const seen = new Set();
  const add = (entry) => {
    if (seen.has(entry.path)) return;
    seen.add(entry.path);
    entries.push(entry);
  };
  items.forEach((item) => {
    add({ path: item.path, label: item.label, icon: item.icon, section: null });
    (item.submenu || []).forEach((sub) => add({ path: sub.path, label: sub.label, icon: sub.icon, section: item.label }));
  });
  extra.forEach(add);
  return entries;
};

// Matches every typed word against label + section ("tick rul" finds
// Tickets > Ticket Rules). Empty query returns everything.
export const filterNav = (entries, query) => {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return entries;
  return entries.filter((entry) => {
    const hay = `${entry.label} ${entry.section || ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
};

// Search API results -> flat, keyboard-friendly items.
export const toSearchItems = ({ customerResults = [], searchResults = {} } = {}) => {
  const items = [];
  const seenCustomers = new Set();
  customerResults.forEach((c) => {
    seenCustomers.add(String(c.id));
    items.push({
      key: `customer-${c.id}`, group: 'Customers', icon: 'fas fa-building',
      title: c.companyName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email,
      subtitle: c.email || '', path: `/customer/${c.id}`
    });
  });
  (searchResults.tickets || []).forEach((t) => items.push({
    key: `ticket-${t.ticket_id}`, group: 'Tickets', icon: 'fas fa-ticket',
    title: `#${t.ticket_number} ${t.subject || ''}`.trim(), subtitle: t.status || '', path: `/tickets/${t.ticket_id}`
  }));
  (searchResults.invoices || []).forEach((inv) => items.push({
    key: `invoice-${inv.id}`, group: 'Invoices', icon: 'fas fa-file-invoice',
    title: inv.invoice_number, subtitle: '', path: `/invoices/editinvoice/${inv.invoice_number}`
  }));
  // The same person can come back from both searches; show them once.
  (searchResults.users || []).filter((u) => !seenCustomers.has(String(u.id))).forEach((u) => items.push({
    key: `user-${u.id}`, group: 'Users', icon: 'fas fa-user',
    title: u.first_name || u.email, subtitle: u.email || '', path: `/customer/${u.id}`
  }));
  (searchResults.purchases || []).forEach((p) => items.push({
    key: `purchase-${p.id}`, group: 'Purchases', icon: 'fas fa-money-bill',
    title: `${p.product_name || ''} ${p.remarks || ''}`.trim(), subtitle: p.invoice_number || '',
    path: `/invoices/editinvoice/${p.invoice_number}`
  }));
  return items;
};
